// Training Reference Library (Session AFP): the office manager for the reference material behind
// each training TYPE. The office uploads, reviews, versions (supersede on re-upload), downloads and
// removes the practical on-site procedures a customer's staff follows (hand-winding and the rest),
// and internal-only procedures alongside them. This is what lets the Director check that the right
// document exists per training type before booking or quoting a session.
//
// Every training type is shown, always: a type with no current document reads "No reference
// document uploaded" rather than being hidden, so a gap is visible at a glance. customerFacing is
// the audience boundary - a "Customer-facing" document is shown in the Client Portal and can be
// emailed to a customer; an "Internal only" document never leaves the office.
//
// THE BOUNDARY (Session AFP): the office uploads real procedure files here. This platform never
// authors procedure content; the library ships empty and the Director uploads the real documents.
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Upload } from "lucide-react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { TRAINING_TYPES, TRAINING_TYPE_LABELS } from "../../lib/trainingTypes";
import type { TrainingReferenceDocumentDto, TrainingType } from "../../lib/trainingTypes";
import { SendTrainingDocModal } from "./SendTrainingDocModal";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const MAX_DOC_MB = 20;
const actionBtn = "max-sm:min-h-11 rounded-ele border px-2.5 py-1 font-menu text-xs tracking-wide transition";

function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

// ---- Upload modal -----------------------------------------------------------

function UploadModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [trainingType, setTrainingType] = useState<TrainingType>("HAND_WINDING");
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("");
  const [customerFacing, setCustomerFacing] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return setError("A title is required.");
    if (!file) return setError("Choose a PDF to upload.");
    if (!isPdfFile(file)) return setError("Only PDF files can be uploaded here.");
    if (file.size > MAX_DOC_MB * 1024 * 1024) return setError(`That file is over ${MAX_DOC_MB}MB. Please upload a smaller one.`);
    setBusy(true);
    setError(null);
    try {
      await api.trainingDocuments.upload(file, { trainingType, title: title.trim(), version: version.trim() || undefined, customerFacing });
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "Could not upload the document. Please try again."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Upload a training reference document" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={submit}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Upload a reference document</h2>
        <div className="grid gap-3">
          <label>
            <span className={labelCls}>Training type</span>
            <select value={trainingType} onChange={(e) => setTrainingType(e.target.value as TrainingType)} className={inputCls}>
              {TRAINING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TRAINING_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className={labelCls}>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Hand-winding procedure card" className={inputCls} />
          </label>
          <label>
            <span className={labelCls}>Version (optional)</span>
            <input value={version} onChange={(e) => setVersion(e.target.value)} maxLength={60} placeholder="e.g. v2 or 2026-07" className={inputCls} />
          </label>
          <fieldset className="rounded-ele border border-ele-purple-accent p-3">
            <legend className={labelCls}>Who is this for</legend>
            <label className="flex items-start gap-2 text-sm text-ele-strong">
              <input type="radio" name="tref-audience" checked={customerFacing} onChange={() => setCustomerFacing(true)} className="mt-1" />
              <span>
                Customer-facing
                <span className="block text-xs text-ele-body">Shown in the Client Portal and can be emailed to a customer.</span>
              </span>
            </label>
            <label className="mt-2 flex items-start gap-2 text-sm text-ele-strong">
              <input type="radio" name="tref-audience" checked={!customerFacing} onChange={() => setCustomerFacing(false)} className="mt-1" />
              <span>
                Internal only
                <span className="block text-xs text-ele-body">Never shown to a customer and never portal-visible.</span>
              </span>
            </label>
          </fieldset>
          <label>
            <span className={labelCls}>File (PDF, up to {MAX_DOC_MB}MB)</span>
            <input
              type="file"
              accept="application/pdf"
              aria-label="Document file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-ele-body file:mr-3 file:rounded-ele file:border-0 file:bg-ele-green file:px-4 file:py-2 file:font-menu file:text-xs file:uppercase file:tracking-wide file:text-ele-bg"
            />
            <p className="mt-1 text-xs text-ele-body opacity-70">
              Uploading a new document for the same type and audience keeps the previous one as history and marks it superseded.
              The training type, file and audience cannot be changed later: remove and upload again to replace them.
            </p>
          </label>
        </div>
        {error && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {error}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---- Edit modal (title + version only) --------------------------------------

function EditModal({ doc, onClose, onDone }: { doc: TrainingReferenceDocumentDto; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState(doc.title);
  const [version, setVersion] = useState(doc.version ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return setError("A title is required.");
    setBusy(true);
    setError(null);
    try {
      await api.trainingDocuments.patch(doc.id, { title: title.trim(), version: version.trim() || null });
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the changes. Please try again."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Edit training reference document" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={submit}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Edit document</h2>
        <p className="mb-3 text-xs text-ele-body opacity-70">
          {doc.trainingTypeLabel}. {doc.customerFacing ? "Customer-facing." : "Internal only."} The training type, file and audience are fixed: remove and re-upload to change them.
        </p>
        <div className="grid gap-3">
          <label>
            <span className={labelCls}>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={inputCls} />
          </label>
          <label>
            <span className={labelCls}>Version (optional)</span>
            <input value={version} onChange={(e) => setVersion(e.target.value)} maxLength={60} className={inputCls} />
          </label>
        </div>
        {error && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {error}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Working..." : "Save"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---- One document row -------------------------------------------------------

function DocRow({
  doc,
  onEdit,
  onRemove,
  onSend,
}: {
  doc: TrainingReferenceDocumentDto;
  onEdit: (d: TrainingReferenceDocumentDto) => void;
  onRemove: (d: TrainingReferenceDocumentDto) => void;
  onSend: (d: TrainingReferenceDocumentDto) => void;
}) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent px-3 py-2 ${doc.superseded ? "opacity-70" : ""}`}>
      <div className="min-w-0">
        <span className="text-sm text-ele-strong">{doc.title}</span>
        {doc.version && <span className="ml-2 text-xs text-ele-body">{doc.version}</span>}
        <StatusPill className="ml-2" word={doc.customerFacing ? "Customer-facing" : "Internal only"} tone={doc.customerFacing ? "green" : "grey"} />
        {doc.superseded && <StatusPill className="ml-2" word={`Superseded ${doc.supersededAtDisplay}`} tone="grey" />}
        <span className="ml-2 text-xs text-ele-body opacity-70">Uploaded {doc.uploadedAtDisplay}</span>
      </div>
      <div className="flex flex-wrap justify-end gap-1.5">
        <a href={api.trainingDocuments.downloadHref(doc.id)} target="_blank" rel="noreferrer" className={`${actionBtn} border-ele-blue text-ele-loler hover:bg-ele-blue/10`}>
          View
        </a>
        {/* Send is offered on CURRENT customer-facing documents only: an internal or superseded
            document is never sent to a customer (the server enforces this too). */}
        {doc.customerFacing && !doc.superseded && (
          <Button variant="secondary" size="sm" onClick={() => onSend(doc)}>
            Send to customer
          </Button>
        )}
        {!doc.superseded && (
          <button type="button" onClick={() => onEdit(doc)} className={`${actionBtn} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>
            Edit
          </button>
        )}
        <button type="button" onClick={() => onRemove(doc)} className={`${actionBtn} border-ele-danger/60 text-ele-danger hover:bg-ele-danger/10`}>
          Remove
        </button>
      </div>
    </div>
  );
}

// ---- Page -------------------------------------------------------------------

export function TrainingReferenceLibraryPage() {
  const [docs, setDocs] = useState<TrainingReferenceDocumentDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TrainingReferenceDocumentDto | null>(null);
  const [sendTarget, setSendTarget] = useState<TrainingReferenceDocumentDto | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TrainingReferenceDocumentDto | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Session AQS, M-3: kept separate from `notice` above rather than widening its shape, since every
  // other call site there is a plain success sentence and a publish result can also be a failure.
  const [publishing, setPublishing] = useState(false);
  const [publishNotice, setPublishNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  const reload = useCallback(() => {
    api.trainingDocuments
      .list({ includeSuperseded: true })
      .then((r) => {
        setDocs(r.documents);
        setLoaded(true);
        setLoadError(false);
      })
      .catch(() => {
        setLoaded(true);
        setLoadError(true);
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Group by type, in the canonical order. Every type is present (even with zero documents) so a
  // gap is visible. Within a type, current documents come first, superseded after.
  const byType = useMemo(() => {
    const current = docs.filter((d) => !d.superseded);
    const superseded = docs.filter((d) => d.superseded);
    return TRAINING_TYPES.map((t) => ({
      type: t,
      label: TRAINING_TYPE_LABELS[t],
      current: current.filter((d) => d.trainingType === t),
      superseded: superseded.filter((d) => d.trainingType === t),
    }));
  }, [docs]);

  // Session AQS, M-3: the office UI restructure audit found this reachable only through Guvnor
  // (the publish_safety_notices MCP tool), never a one-click office action even though the two
  // notices (hand-winding, emergency release) are committed, shipped files. Shares the same
  // creation path an ordinary upload uses, so it supersedes any existing current document of the
  // same training type and audience exactly as a manual re-upload would -- the branded confirm
  // says so before it runs.
  async function publishSafetyNotices() {
    setPublishing(true);
    setPublishNotice(null);
    try {
      const res = await api.trainingDocuments.publishSafetyNotices();
      const failed = res.results.filter((r) => r.status === "failed");
      setPublishNotice(
        failed.length > 0
          ? { tone: "error", text: `Published ${res.results.length - failed.length} of ${res.results.length}. ${failed.map((r) => `${r.reference}: ${r.detail}`).join(" ")}` }
          : { tone: "success", text: "Both safety notices are published: hand-winding and emergency release." },
      );
      reload();
    } catch (err) {
      setPublishNotice({ tone: "error", text: friendlyApiError(err, "Could not publish the safety notices. Please try again.") });
    } finally {
      setPublishing(false);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await api.trainingDocuments.remove(removeTarget.id);
      setRemoveTarget(null);
      setNotice(`"${removeTarget.title}" removed.`);
      reload();
    } catch (err) {
      setRemoveError(friendlyApiError(err, "Could not remove the document. Please try again."));
    } finally {
      setRemoveBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Training documents"
        subtitle="The reference material behind each training type: the on-site procedures a customer's staff follows, and internal-only procedures. Customer-facing documents can be sent to a customer and appear in their Client Portal. Review the right document exists here before you book or quote a training session."
        actions={
          <>
          <ConfirmButton
            onConfirm={publishSafetyNotices}
            label="Publish safety notices"
            confirmLabel="Confirm?"
            busy={publishing}
            busyLabel="Publishing..."
            className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
            title="Publishes the two built-in notices (hand-winding, emergency release) from their committed PDFs. Supersedes any existing current document of the same type and audience."
          />
            <Button size="sm" onClick={() => setUploadOpen(true)} icon={<Upload aria-hidden size={15} />}>
              Upload reference document
            </Button>
          </>
        }
      />

      {notice && (
        <p role="status" className="mb-4 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {publishNotice && (
        <p role="status" className={`mb-4 rounded-ele border px-3 py-2 text-sm ${publishNotice.tone === "success" ? "border-ele-green text-ele-strong" : "border-ele-danger text-ele-danger"}`}>
          {publishNotice.text}
        </p>
      )}

      {!loaded ? (
        <Loader />
      ) : loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          Could not load the reference documents. Refresh to try again.
        </p>
      ) : (
        <>
          <label className="mb-4 flex items-center gap-2 text-sm text-ele-body">
            <input type="checkbox" checked={showSuperseded} onChange={(e) => setShowSuperseded(e.target.checked)} />
            Show superseded versions
          </label>
          <div className="space-y-5">
            {byType.map((group) => (
              <section key={group.type} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
                <h2 className="mb-3 font-heading text-lg text-ele-blue">{group.label}</h2>
                {group.current.length === 0 ? (
                  <p className="rounded-ele border border-dashed border-ele-purple-accent px-3 py-3 text-sm text-ele-body">
                    No reference document uploaded for this type.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {group.current.map((d) => (
                      <DocRow key={d.id} doc={d} onEdit={setEditTarget} onRemove={setRemoveTarget} onSend={setSendTarget} />
                    ))}
                  </div>
                )}
                {showSuperseded && group.superseded.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">Previous versions</p>
                    <div className="space-y-2">
                      {group.superseded.map((d) => (
                        <DocRow key={d.id} doc={d} onEdit={setEditTarget} onRemove={setRemoveTarget} onSend={setSendTarget} />
                      ))}
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        </>
      )}

      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onDone={() => {
            setUploadOpen(false);
            setNotice("Reference document uploaded.");
            reload();
          }}
        />
      )}
      {editTarget && (
        <EditModal
          doc={editTarget}
          onClose={() => setEditTarget(null)}
          onDone={() => {
            setEditTarget(null);
            setNotice("Document updated.");
            reload();
          }}
        />
      )}
      {sendTarget && (
        <SendTrainingDocModal
          doc={sendTarget}
          onClose={() => setSendTarget(null)}
          onSent={(msg) => {
            setSendTarget(null);
            setNotice(msg);
          }}
        />
      )}
      {/* Soft delete (the row + Drive file are kept): the archive framing avoids the destructive
          variant's false "permanent and cannot be undone" copy, and twoStep keeps the required
          two-step confirmation (the AFL opt-in). */}
      <ConfirmDeleteModal
        open={removeTarget !== null}
        title="Remove reference document"
        message="Remove this reference document?"
        warning={
          removeTarget
            ? `"${removeTarget.title}" will no longer appear here or in the Client Portal, and can no longer be sent to a customer. The file itself stays in Google Drive.`
            : null
        }
        confirmLabel="Remove"
        variant="archive"
        twoStep
        busy={removeBusy}
        error={removeError}
        onConfirm={confirmRemove}
        onCancel={() => {
          if (!removeBusy) {
            setRemoveTarget(null);
            setRemoveError(null);
          }
        }}
      />
    </div>
  );
}
